import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostBinding,
  inject,
  input,
  OnInit,
} from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { Router, RouterLink } from '@angular/router'
import { Uuid } from '@ftr/contracts/type/shared'
import { GetCourtSystemService } from '@ftr/data-court-system'
import {
  ApiResult,
  IllustrationComponent,
  NotificationModule,
  PageComponent,
  RouterActivityService,
  selectWith,
  TertiaryButtonComponent,
  toRemoteSignal,
} from '@ftr/foundation'
import { AppPaths, CourtSystemAdminPaths, CourtSystemPaths } from '@ftr/routing-paths'
import { featureFlag, LaunchDarklyModule, LaunchDarklyService } from '@ftr/ui-feature-flags'
import { AuthenticationService, GetCurrentUserAction, UserState } from '@ftr/ui-user'
import { WindowRefService } from '@ftr/window'
import { Store } from '@ngxs/store'
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs'

export const defaultForbiddenError =
  'Sorry, you do not have access to this page. Please use the navigation menu to select another page.'

export const publicAccessEnabledForbiddenError =
  'Sorry, you do not have access to this page. If you believe you should have access, please contact your administrator for assistance.'

const PROD_COURT_SYSTEMS_FTR_STAFF_CANT_ACCESS = [
  // Kansas 9th Judicial District
  '270b6f36-f4ab-45c6-b4bd-627be9ae2da3',
  // Trinidad & Tobago
  '06efb090-a7ab-4586-9d0b-842112124e54',
]

@Component({
  selector: 'ftr-forbidden',
  templateUrl: './forbidden.component.html',
  styleUrls: ['./forbidden.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    NotificationModule,
    PageComponent,
    LaunchDarklyModule,
    RouterLink,
    IllustrationComponent,
    TertiaryButtonComponent,
  ],
})
export class ForbiddenComponent implements OnInit {
  private readonly routerActivityService = inject(RouterActivityService)
  private readonly windowRefService = inject(WindowRefService)
  private readonly launchDarklyService = inject(LaunchDarklyService)
  private readonly authenticationService = inject(AuthenticationService)
  private readonly router = inject(Router)
  private readonly store = inject(Store)
  private readonly getCourtSystemService = inject(GetCourtSystemService)

  readonly courtSystemId = input<Uuid>()

  protected readonly addUserRouterLink = computed(() =>
    this.courtSystemId()
      ? ['/', AppPaths.CourtSystem, this.courtSystemId()!, CourtSystemPaths.Admin, CourtSystemAdminPaths.Users, 'add']
      : null,
  )

  protected readonly addUserQueryParams = computed(() => {
    if (!this.addUserRouterLink()) {
      return undefined
    }

    return {
      redirectUrl: this.windowRefService.history().state?.originalUrl,
      prefillEmail: this.currentUserEmail().unwrapOr(undefined),
    }
  })

  protected readonly isForbiddenCourtSystemForFtrStaff = computed(() =>
    PROD_COURT_SYSTEMS_FTR_STAFF_CANT_ACCESS.find(i => i === this.courtSystemId()) ? true : false,
  )
  protected readonly isGlobalAdmin = selectWith(() => UserState.isGlobalAdmin)
  private readonly currentUserEmail = selectWith(() => UserState.currentUserEmail)

  /**
   * Fetches the court system directly. The member guard redirects here before route resolvers run,
   * so CourtSystemsState does not hold it.
   */
  protected readonly courtSystemRemote = toRemoteSignal(
    toObservable(computed(() => (this.isGlobalAdmin() ? this.courtSystemId() : undefined))).pipe(
      switchMap(id => (id ? this.getCourtSystemService.getCourtSystem(id) : ApiResult.notAsked())),
    ),
  )

  private readonly publicNavigationEnabled = featureFlag('public-navigation')

  readonly #destroyRef = inject(DestroyRef)

  errorMessage: string

  @HostBinding('class.forbidden--public-navigation')
  protected get isPublicNavigationEnabled(): boolean {
    return this.publicNavigationEnabled()
  }

  async ngOnInit(): Promise<void> {
    await this.routerActivityService.waitForRedirect()
    const state = this.windowRefService.history().state
    this.authenticationService.initialize().then(() => {
      if (this.publicNavigationEnabled()) {
        this.errorMessage = state?.userMessage || publicAccessEnabledForbiddenError
      } else if (!this.authenticationService.getCurrentUserId()) {
        this.errorMessage = state?.userMessage || defaultForbiddenError
      } else {
        this.errorMessage = state?.userMessage || defaultForbiddenError
        this.store.dispatch(new GetCurrentUserAction())
        this.launchDarklyService
          .observeFlagChanges<boolean>('detailed-error-messages')
          .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
          .subscribe(detailedErrorMessageEnabled => {
            this.errorMessage = detailedErrorMessageEnabled
              ? state?.developerMessage || state?.userMessage || defaultForbiddenError
              : state?.userMessage || defaultForbiddenError
          })
      }
    })
  }

  goToHome(): void {
    this.router.navigate(['/'])
  }
}
